import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { Session, UserSession } from '../types';
import { getValidAccessToken } from '../services/authService';

interface SidebarProps {
  collapsed: boolean;
  toggleCollapse: () => void;
  userSession?: UserSession; // Add user session for role-based filtering
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, toggleCollapse, userSession }) => {
  const SESSIONS_API_URL = `${process.env.REACT_APP_URL}/chat_application/v1/sessions`;
  const { sessionId } = useParams<{ sessionId?: string }>();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false); // Track history visibility state

  const handleHistoryClick = async () => {
    const accessToken = await getValidAccessToken();
    const orgId = localStorage.getItem('org_id');

    try {
      if (accessToken) {
        setIsLoading(true);
        const response = await fetch(SESSIONS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ org_id: orgId })
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch sessions: ${response.status}`);
        }

        const data = await response.json();
        const apiSessions = Array.isArray(data?.sessions) ? data.sessions : [];

        const mappedSessions: Session[] = apiSessions
          .filter((item: any) => item && item.session_id)
          .map((item: any) => ({
            id: item.session_id,
            sessionName: item.session_name || 'Untitled Session',
            domainName: item.domain_name || '',
            createdAt: item.created_at ? new Date(item.created_at) : new Date(),
            createdBy: item.created_by || item.user_id || 'Unknown',
            adminId: data?.org_id || orgId || undefined
          }));

        setSessions(mappedSessions);
      }
    } catch (error) {
      console.error('Error calling sessions API:', error);
      setSessions([]);
    } finally {
      setIsLoading(false);
      setShowHistory(!showHistory);
    }
  };

  /**
   * Auto-collapse sidebar when mouse leaves the sidebar area
   * Improves UX by keeping the sidebar out of the way when not in use
   */
  const handleMouseLeave = () => {
    if (!collapsed) {
      toggleCollapse();
    }
  };

  /**
   * Auto-expand sidebar when mouse enters the sidebar area
   * Provides quick access to sidebar content on hover
   */
  const handleMouseEnter = () => {
    if (collapsed) {
      toggleCollapse();
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  return (
    <div
      className={`sidebar ${collapsed ? 'collapsed' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo */}
      {!collapsed && (
        <div className="sidebar-logo" onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>
          <div className="sidebar-logo-icon" style={{ backgroundColor: 'white', color: '#000EAB' }}>
            <i className="bi bi-shield-check"></i>
          </div>
          <div className="sidebar-logo-text">
            Fair Design Agent
          </div>
        </div>
      )}

      {/* Menu Items */}
      <div className="sidebar-menu">
        {/* Dashboard - Always at top */}
        <button
          className="sidebar-menu-item"
          onClick={() => window.location.href = '/'}
        >
          <i className="bi bi-grid-3x3-gap"></i>
          {!collapsed && <span className="menu-text">Dashboard</span>}
        </button>

        {/* History - Always after Dashboard */}
        <button
          className={`sidebar-menu-item ${showHistory ? 'active' : ''}`}
          onClick={handleHistoryClick}
        >
          <i className="bi bi-clock-history"></i>
          {!collapsed && <span className="menu-text">History</span>}
        </button>

        {/* Session List - Show right after History button when history is toggled on */}
        {!collapsed && showHistory && (
          <div className="sidebar-sessions">
            {isLoading && sessions.length === 0 ? (
              <div className="d-flex justify-content-center my-4">
                <Spinner animation="border" size="sm" style={{ color: 'var(--primary-blue)' }} />
              </div>
            ) : (
              <>
                {sessions.length === 0 ? (
                  <div className="text-center text-muted py-4" style={{ fontSize: '12px' }}>
                    No sessions yet
                  </div>
                ) : (
                  // Show all sessions with scrolling
                  sessions.map(session => (
                    <Link
                      key={session.id}
                      to={`/session/${session.id}`}
                      state={{ sessionName: session.sessionName }}
                      className={`sidebar-session-item ${sessionId === session.id ? 'active' : ''}`}
                    >
                      <div className="sidebar-session-title">{session.sessionName}</div>
                      <div className="sidebar-session-detail">
                        Created: {formatDate(session.createdAt)}
                      </div>
                      <div className="sidebar-session-detail">
                        Created by: {session.createdBy}
                      </div>
                      {session.domainName && (
                        <div className="sidebar-session-detail">
                          Domain: {session.domainName}
                        </div>
                      )}
                      {session.endedAt && (
                        <div className="sidebar-session-detail">
                          Ended: {formatDate(session.endedAt)}
                        </div>
                      )}
                    </Link>
                  ))
                )}
              </>
            )}
          </div>
        )}

        {/* Projects - Show after sessions (if history expanded) or after history button (if history collapsed) */}
        <button
          className="sidebar-menu-item"
          onClick={() => window.location.href = '/projects'}
        >
          <i className="bi bi-kanban"></i>
          {!collapsed && <span className="menu-text">Projects</span>}
        </button>

        <Link to="/admin" className="sidebar-menu-item" style={{ textDecoration: 'none' }}>
          <i className="bi bi-gear"></i>
          <span className="menu-text">Admin Settings</span>
        </Link>

      </div>

      {/* Bottom Buttons - Fixed to bottom
      {!collapsed && (
        <div className="sidebar-bottom-buttons">
          
        </div>
      )} */}
    </div>
  );
};

export default Sidebar;
